import { Eye, X } from 'lucide-react'
import { Badge, Button, IconButton, Tooltip } from '@/components/play'
import type { PublicProfile } from '@/lib/watch/types'
import { formatAudience } from '@/lib/watch/rules'
import { useAudience } from '@/lib/watch/useWatchScreen'
import { HostAvatar } from './HostAvatar'
import './host-map.css'

/** The host's first name, or what names them when there is none ("Shane", the handle, "Host"). */
const firstName = (host: PublicProfile) => host.name.split(/\s+/)[0] || host.name

export interface FollowHostProps {
  host: PublicProfile
  /** The map moves with the host's. */
  following: boolean
  onFollowingChange(following: boolean): void
}

/**
 * Follow a host's map (docs/features/follow-host-map.md): while the stage's call is the host's map, the top of the
 * map says so with the host's face. Pressed, the map moves with theirs ("Following Shane"); a touch on the map lets
 * it go ("Follow Shane"), and a press brings it back. Labelled, since following is unfamiliar; the theme's pressed
 * treatment says it's on.
 */
export function FollowHost({ host, following, onFollowingChange }: FollowHostProps) {
  const name = firstName(host)
  return (
    <Tooltip content={`Move with ${name}’s map`}>
      <Button variant="secondary" size="compact" elevated aria-pressed={following} className="watch-host-map__follow" onClick={() => onFollowingChange(!following)}>
        <HostAvatar host={host} size="xs" aria-hidden="true" />
        {following ? `Following ${name}` : `Follow ${name}`}
      </Button>
    </Tooltip>
  )
}

export interface HostMapLiveProps {
  /** Stop showing the map: clears the call. Return a promise to hold the button while it goes. */
  onStop(): void | Promise<unknown>
}

/**
 * A live host's own map while it is their stage's call: their audience sees it move with them. Live, with how many
 * are watching besides them (nobody can tell who follows: viewers send nothing into the room), and Stop showing your
 * map. Counted here, where the count shows, not across the page.
 */
export function HostMapLive({ onStop }: HostMapLiveProps) {
  const { othersWatching } = useAudience()
  return (
    <div className="watch-host-map__live" role="status">
      <Badge tone="live">Live to your audience</Badge>
      <span className="watch-host-map__audience">
        <Eye aria-hidden="true" />
        {formatAudience(othersWatching)}
        <span className="sr-only"> watching</span>
      </span>
      {/* A refused stop leaves the tag up: the map is still the call. */}
      <IconButton label="Stop showing your map" variant="ghost" size="compact" onClick={() => void Promise.resolve(onStop()).catch(() => undefined)}>
        <X />
      </IconButton>
    </div>
  )
}
